import dayjs from 'dayjs';
import React from 'react';

import { ProductSubCategory } from '@getethos/ethos-global-lib';
import { get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import Tooltip from '@mui/material/Tooltip';
import { createSlice } from '@reduxjs/toolkit';

import { AppThunk } from '../../../app/store';
import {
  applicationDecisionEditEndPoint,
  applicationDecisionEditV2EndPoint,
  applicationDecisionEndPoint,
  applicationDecisionLeadingToCLREditEndPoint,
  applicationDecisionV2EndPoint,
  createCarrierDecision,
  sendEmailEndPoint,
  uploadLetterEndPoint,
  validateMIBCodesEndPoint,
} from '../../../common/api.endPoints';
import { Carriers } from '../../../common/constants/carriers';
import { Decision, DecisionType } from '../../../common/constants/decision';
import { DocumentType } from '../../../common/constants/documentType';
import { TaskState } from '../../../common/constants/states';
import { HttpService } from '../../../common/http.service';
import {
  ISnacksProps,
  SnackBarType,
} from '../../layout/interfaces/ISnacksProps';
import { taskListActions } from '../../tasks/slice/taskListSlice';
import {
  addTasks,
  refreshTasks,
  updateTasksState,
  updateTaskState,
} from '../../tasks/slice/taskThunks';
import { TaskTypes } from '../../tasks/types/taskTypes';
import { detailActions, syncApplicationState } from '../detailSlice';
import { IDecision } from '../interfaces/IDecision';
import { IDecisionRequest } from '../interfaces/IDecisionRequest';
import { IDecisionUpdateRequest } from '../interfaces/IDecisionUpdateRequest';
import {
  LETTER_DEFAULT_EMAIL,
  LETTER_DEFAULT_PHONE,
} from '../tasks/letter/generator/constants';
import { generateLetterFile } from '../tasks/letter/generator/hooks/useGenerate';
import { Data, LetterTypes } from '../tasks/letter/generator/types';
import {
  getLetterRecipientFullName,
  getProposedInsuredName,
} from '../tasks/letter/generator/utils';

export interface AutoSendOptions {
  enabled: boolean;
  openSnacks: ISnacksProps[];
  updateSnacks: (updatedSnacks: ISnacksProps[]) => void;
}

export const decisionSlice = createSlice({
  name: 'applicationDetails',

  initialState: {
    decisions: [] as IDecision[],
    createDecisionFailed: false,
    createDecisionError: '',
    createDecisionErrorType: '',
    validationResult: {},
    validationFailed: false,
    validationError: '',
  },

  reducers: {
    onDecisionFetchSuccess: (state, action) => {
      state.decisions = action.payload.response as IDecision[];
    },
    onDecisionSuccess: (state, action) => {
      state.decisions.push(action.payload.decision);
    },
    onDecisionFailed: (state, action) => {
      state.createDecisionFailed = true;
      state.createDecisionError = action.payload.message;
      state.createDecisionErrorType = action.payload.type;
    },
    onDecisionUpdate: (state, action) => {
      const editedDecision = state.decisions.find(
        d => d.id === action.payload.decision.id,
      );

      if (editedDecision) {
        editedDecision.deleteReason = action.payload.decision.deleteReason;
        editedDecision.reviewStatus = action.payload.decision.reviewStatus;
      } else {
        console.warn('Edited decision not found', action.payload.decision);
      }
    },
    onCodebackValidation: (state, action) => {
      state.validationResult = action.payload;
      state.validationFailed = false;
    },
    onCodebackValidationFailed: (state, action) => {
      state.validationResult = {};
      state.validationError = action.payload?.message;
      state.validationFailed = true;
    },
  },
});

export const fetchDecisions =
  (
    applicationId: string,
    setDecisionFetchStatus: (status: string) => void,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    setDecisionFetchStatus('loading');
    const response = await HttpService.getInstance().get(
      applicationDecisionEndPoint(applicationId),
    );

    if (response && !response.error) {
      const decision = response.parsedBody;
      dispatch(decisionActions.onDecisionFetchSuccess({ response: decision }));
      setDecisionFetchStatus('success');
    } else {
      setDecisionFetchStatus('success');
      if (response && response.error) {
        setSnacksCallback([
          ...openSnacks,
          {
            id: uuidv4(),
            type: response.error.type,
            message: response.error.message,
          },
        ]);
      }
    }
  };

// Helper function to refetch all decisions without status callbacks
export const refetchDecisions =
  (applicationId: string): AppThunk =>
  async dispatch => {
    const response = await HttpService.getInstance().get(
      applicationDecisionEndPoint(applicationId),
    );

    if (response && !response.error) {
      const decisions = response.parsedBody;
      dispatch(decisionActions.onDecisionFetchSuccess({ response: decisions }));
    }
  };

export const postDecision =
  (
    applicationId: string,
    decision: IDecisionRequest,
    isCarrierDecision?: boolean,
    useV2?: boolean,
    autoSendOptions?: AutoSendOptions,
  ): AppThunk =>
  async dispatch => {
    const requestBody = { ...decision };
    let response;
    if (isCarrierDecision) {
      response = await HttpService.getInstance().post(
        createCarrierDecision(applicationId),
        requestBody,
      );
    } else {
      const endpoint = useV2
        ? applicationDecisionV2EndPoint(applicationId)
        : applicationDecisionEndPoint(applicationId);
      response = await HttpService.getInstance().post(endpoint, requestBody);
    }

    if (response && !response.error) {
      const decisionResponse = response.parsedBody;
      // dispatch(
      //   detailActions.onApplicationStageUpdate({
      //     applicationStage: decisionResponse.applicationStage,
      //     applicationLifecycle: decisionResponse.applicationLifecycle,
      //     isRecommendedForRescission:
      //       decisionResponse.isRecommendedForRescission,
      //   }),
      // );
      dispatch(syncApplicationState(applicationId));
      // Refetch all decisions to ensure UI shows all decisions created (e.g., PIA + PLAN_F)
      dispatch(refetchDecisions(applicationId));
      if (isCarrierDecision) {
        dispatch(
          detailActions.onCarrierLifecycleUpdate({
            carrierUnderwriterId: 0,
            carrierUnderwriterDisplayName: '',
          }),
        );
      }
      if (decisionResponse.tasks && decisionResponse.tasks.length > 0) {
        dispatch(addTasks(decisionResponse.tasks));
      }
      dispatch(refreshTasks(applicationId));
      if (
        decisionResponse.updatedTasks &&
        decisionResponse.updatedTasks.length > 0
      ) {
        dispatch(updateTasksState(decisionResponse.updatedTasks));
      }

      if (
        autoSendOptions?.enabled &&
        decisionResponse.tasks &&
        decisionResponse.tasks.length > 0
      ) {
        await dispatch(
          autoSendReinstatementLetter(
            applicationId,
            decision,
            decisionResponse.tasks,
            autoSendOptions.openSnacks,
            autoSendOptions.updateSnacks,
          ),
        );
      }
    } else {
      dispatch(
        decisionActions.onDecisionFailed(response ? response.error : undefined),
      );
    }
  };

const autoSendReinstatementLetter =
  (
    applicationId: string,
    decision: IDecisionRequest,
    newTasks: { id: number; taskType: string; state?: string }[],
    openSnacks: ISnacksProps[],
    updateSnacks: (updatedSnacks: ISnacksProps[]) => void,
  ): AppThunk =>
  async (dispatch, getState) => {
    if (decision.decisionType !== DecisionType.REINSTATEMENT) return;

    const isApproval = decision.decision === Decision.APPROVED;
    const isWithdrawal = decision.decision === Decision.WITHDRAWN;
    if (!isApproval && !isWithdrawal) return;

    const expectedTaskType = isApproval
      ? TaskTypes.REINSTATEMENT_APPROVAL_LETTER
      : TaskTypes.REINSTATEMENT_WITHDRAWN_LETTER;
    const expectedDocumentType = isApproval
      ? DocumentType.REINSTATEMENT_APPROVAL_LETTER
      : DocumentType.REINSTATEMENT_WITHDRAWN_LETTER;
    const letterType = isApproval
      ? LetterTypes.reinstatementApproval
      : LetterTypes.reinstatementWithdrawal;

    // Decision response may include historical tasks of the same type from prior decisions.
    // The just-created task is always in ORDERED state — pick the newest one by id.
    const letterTask = newTasks
      .filter(
        t => t.taskType === expectedTaskType && t.state === TaskState.ORDERED,
      )
      .sort((a, b) => b.id - a.id)[0];
    if (!letterTask) return;

    const pushAlert = (message: string) => {
      updateSnacks([
        ...openSnacks,
        { id: uuidv4(), type: SnackBarType.ALERT, message },
      ]);
    };

    dispatch(taskListActions.startAutoSend({ taskId: letterTask.id }));
    let uploaded = false;
    // Outer try/finally guarantees endAutoSend dispatches on every exit path
    // (success, early-return on upload failure, thrown exception, send failure)
    // so the "Generating and sending letter..." UI placeholder always clears.
    try {
      try {
        const app = getState().application;
        const isJuvenile = app.subCategory === ProductSubCategory.JUVENILE;
        const fullName = getLetterRecipientFullName(
          isJuvenile,
          app.policyholderName,
          app.customerName,
        );
        const insuredName =
          getProposedInsuredName(isJuvenile, app.beneficiaryDetails) ?? '';

        const data: Data = {
          source: null,
          letterType,
          letter: {} as Data['letter'],
          phone: LETTER_DEFAULT_PHONE,
          email: LETTER_DEFAULT_EMAIL,
          date: dayjs().format('YYYY-MM-DD'),
          carrier: app.carrier as Carriers,
          fullName,
          policyNumber: app.carrierPolicyNumber ?? '',
          riders: [],
          isJuvenile,
          insuredName,
        };

        const [file] = await generateLetterFile(data, {
          isFCRAIncluded: false,
          openInNewTab: false,
        });

        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', expectedDocumentType);
        formData.append('documentSize', file.size.toString());

        const uploadResponse = await HttpService.getInstance().postFiles(
          uploadLetterEndPoint(applicationId, letterTask.id),
          formData,
        );
        if (!uploadResponse) {
          pushAlert(
            'Reinstatement letter auto-generation failed. Use Generate manually.',
          );
          return;
        }
        dispatch(taskListActions.addAttachment(uploadResponse));
        uploaded = true;
      } catch (e) {
        console.warn(
          '[reinstatement letter auto-send] generate/upload failed:',
          e instanceof Error ? e.message : String(e),
        );
        pushAlert(
          'Reinstatement letter auto-generation failed. Use Generate manually.',
        );
        return;
      }

      try {
        const app = getState().application;
        const sendResponse = await HttpService.getInstance().patch(
          sendEmailEndPoint(applicationId, letterTask.id, app.termPolicyId),
        );
        const isSent =
          sendResponse?.parsedBody?.labelIds?.includes('SENT') ?? false;
        const newState = isSent ? TaskState.COMPLETED : TaskState.FOR_REVIEW;
        dispatch(
          updateTaskState(
            applicationId,
            letterTask.id,
            newState,
            openSnacks,
            updateSnacks,
          ),
        );
      } catch (e) {
        console.warn(
          '[reinstatement letter auto-send] send failed:',
          e instanceof Error ? e.message : String(e),
        );
        if (uploaded) {
          pushAlert(
            'Reinstatement letter uploaded but auto-send failed. Send manually.',
          );
        }
      }
    } finally {
      dispatch(taskListActions.endAutoSend({ taskId: letterTask.id }));
    }
  };

export const postDecisionReview =
  (
    applicationId: string,
    decisionId: string,
    reviewStatus: 'Pending' | 'Approved' | 'Denied',
  ): AppThunk =>
  async dispatch => {
    const requestBody = { reviewStatus };

    const response = await HttpService.getInstance().patch(
      `${applicationDecisionEditEndPoint(applicationId, decisionId)}/review`,
      requestBody,
    );

    if (response && !response.error) {
      const reviewResponse = response.parsedBody;

      if (reviewResponse.tasks && reviewResponse.tasks.length > 0) {
        dispatch(addTasks(reviewResponse.tasks));
      }
      if (
        reviewResponse.updatedTasks &&
        reviewResponse.updatedTasks.length > 0
      ) {
        dispatch(updateTasksState(reviewResponse.updatedTasks));
      }

      dispatch(syncApplicationState(applicationId));
      // Refetch all decisions to ensure UI shows all decisions
      dispatch(refetchDecisions(applicationId));
    } else {
      dispatch(
        decisionActions.onDecisionFailed(response ? response.error : undefined),
      );
    }
  };

export const patchDecision =
  (
    applicationId: string,
    decisionId: string,
    decisionUpdate: IDecisionUpdateRequest,
    useV2?: boolean,
  ): AppThunk =>
  async dispatch => {
    const requestBody = { ...decisionUpdate };

    const endpoint = useV2
      ? applicationDecisionEditV2EndPoint(applicationId, decisionId)
      : applicationDecisionEditEndPoint(applicationId, decisionId);
    const response = await HttpService.getInstance().patch(
      endpoint,
      requestBody,
    );

    if (response && !response.error) {
      const decisionResponse = response.parsedBody;
      // dispatch(
      //   detailActions.onApplicationStageUpdate({
      //     applicationStage: decisionResponse.newDecision.applicationStage,
      //     applicationLifecycle:
      //       decisionResponse.newDecision.applicationLifecycle,
      //     isRecommendedForRescission:
      //       decisionResponse.newDecision.isRecommendedForRescission,
      //   }),
      // );
      dispatch(syncApplicationState(applicationId));
      // Refetch all decisions to ensure UI shows all decisions
      dispatch(refetchDecisions(applicationId));
      if (decisionResponse.newDecision.tasks?.length > 0) {
        dispatch(addTasks(decisionResponse.newDecision.tasks));
      }
    } else {
      dispatch(
        decisionActions.onDecisionFailed(response ? response.error : undefined),
      );
    }
  };

export const patchDecisionLeadingToCLR =
  (
    applicationId: string,
    decisionId: string,
    decisionUpdate: IDecisionUpdateRequest,
  ): AppThunk =>
  async dispatch => {
    const requestBody = { ...decisionUpdate };

    const response = await HttpService.getInstance().patch(
      applicationDecisionLeadingToCLREditEndPoint(applicationId, decisionId),
      requestBody,
    );

    if (response && !response.error) {
      dispatch(syncApplicationState(applicationId));
      dispatch(refetchDecisions(applicationId));
    } else {
      dispatch(
        decisionActions.onDecisionFailed(response ? response.error : undefined),
      );
    }
  };

export const validateMIBCodes =
  (applicationId: string, codes: string[]): AppThunk =>
  async dispatch => {
    const response = await HttpService.getInstance().post(
      validateMIBCodesEndPoint(applicationId),
      { codes },
    );
    if (response && !response.error) {
      const validationResult = response.parsedBody;
      dispatch(decisionActions.onCodebackValidation({ validationResult }));
    } else {
      dispatch(decisionActions.onCodebackValidationFailed(response?.error));
    }
  };

export function getValidationResult(
  code: string,
  codeback: any,
  resultMap: Map<string, object>,
): any {
  if (resultMap?.size > 0 === false) {
    const validationResult = get(codeback, 'validationResult.validationResult');
    if (validationResult && get(validationResult, 'disabled') === 'disabled') {
      return <></>;
    } else if (validationResult && validationResult instanceof Array) {
      validationResult?.forEach((row: any[]) => {
        if (row?.length > 1) {
          resultMap.set(row[0], row[1]);
        }
      });
    }
  }

  const result = resultMap?.get(code) || {};
  if (get(result, 'isValid') === true) {
    return (
      <Tooltip title={'Valid code'}>
        <CheckRoundedIcon
          color={'success'}
          sx={{ marginLeft: '15px', marginBottom: '-5px' }}
        ></CheckRoundedIcon>
      </Tooltip>
    );
  } else if (get(result, 'isValid') === false) {
    const messages = get(result, 'messages', []).join('; ');
    return (
      <Tooltip title={'Invalid code, you could still submit: ' + messages}>
        <ClearRoundedIcon
          color={'error'}
          sx={{ marginLeft: '15px', marginBottom: '-5px' }}
        ></ClearRoundedIcon>
      </Tooltip>
    );
  }
  return <></>;
}

export const decisionReducer = decisionSlice.reducer;
export const decisionActions = decisionSlice.actions;
